import {
  SEEKING_TYPES,
  normalizeSeekingPreferenceType,
} from '../constants/seeking';

export const BAND_SEEKING_FIELD = 'seekingMembers';

export const BAND_SEEKING_ALLOWED_TYPES = Object.freeze([
  SEEKING_TYPES.MEMBERS,
  SEEKING_TYPES.NONE,
]);

const MAX_BAND_SEEKING_MEMBERS = 24;

const toCleanString = (value) => (
  typeof value === 'string' ? value.trim() : ''
);

const toPlainObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const titleizeInstrumentKey = (key) => (
  String(key || '')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
);

const isTruthyFlag = (value) => (
  value === true
  || value === 1
  || (typeof value === 'string' && value.trim().toLowerCase() === 'true')
);

const isExplicitFalseFlag = (value) => (
  value === false
  || value === 0
  || (typeof value === 'string' && value.trim().toLowerCase() === 'false')
);

export const normalizeBandSeekingMembers = (members) => (
  Array.isArray(members)
    ? members
      .slice(0, MAX_BAND_SEEKING_MEMBERS)
      .map((member, index) => {
        const row = toPlainObject(member);
        const instrument = toCleanString(row.instrument);
        const experience = toCleanString(row.experience);
        const note = toCleanString(row.note);
        const id = toCleanString(row.id) || `band-seeking-member-${index}`;

        return { id, instrument, experience, note };
      })
    : []
);

const buildMembersFromNeededMap = (instrumentsNeeded) => (
  Object.entries(toPlainObject(instrumentsNeeded))
    .filter(([, value]) => isTruthyFlag(value))
    .slice(0, MAX_BAND_SEEKING_MEMBERS)
    .map(([key]) => ({
      id: `band-needed-${key}`,
      instrument: titleizeInstrumentKey(key),
      experience: '',
      note: '',
    }))
    .filter((member) => member.instrument)
);

const createEmptyBandSeekingDraft = () => ({
  type: SEEKING_TYPES.NONE,
  members: [],
  bandStyles: [],
  notes: '',
  openToJam: false,
});

const hasFilledMemberRows = (members) => (
  members.some((member) => member.instrument)
);

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

export const normalizeBandSeekingDraft = ({
  seekingMembers,
  instrumentsNeeded,
} = {}) => {
  const source = toPlainObject(seekingMembers);
  const sourceType = normalizeSeekingPreferenceType(source.type, null);

  if (sourceType === SEEKING_TYPES.NONE || sourceType === SEEKING_TYPES.BAND) {
    return createEmptyBandSeekingDraft();
  }

  if (sourceType !== SEEKING_TYPES.MEMBERS && isExplicitFalseFlag(source.open)) {
    return createEmptyBandSeekingDraft();
  }

  const hasExplicitMembers = hasOwn(source, 'members');
  const payloadMembers = hasExplicitMembers ? normalizeBandSeekingMembers(source.members) : [];
  const resolvedMembers = hasExplicitMembers
    ? payloadMembers
    : buildMembersFromNeededMap(instrumentsNeeded);
  const notes = toCleanString(source.notes);
  const isOpen = isTruthyFlag(source.open);

  if (
    sourceType === SEEKING_TYPES.MEMBERS
    || isOpen
    || hasFilledMemberRows(resolvedMembers)
    || notes
  ) {
    return {
      type: SEEKING_TYPES.MEMBERS,
      members: resolvedMembers,
      bandStyles: [],
      notes,
      openToJam: false,
    };
  }

  return createEmptyBandSeekingDraft();
};

export const getActiveBandSeekingDetails = (payload) => {
  const draft = normalizeBandSeekingDraft(payload);
  return draft.type === SEEKING_TYPES.MEMBERS ? draft : null;
};

export const serializeBandSeekingMembersPayload = (draft) => {
  const normalizedType = normalizeSeekingPreferenceType(draft?.type, SEEKING_TYPES.NONE);
  if (normalizedType !== SEEKING_TYPES.MEMBERS) {
    return {
      type: SEEKING_TYPES.NONE,
      open: false,
      members: [],
      bandStyles: [],
      notes: '',
      openToJam: false,
    };
  }

  return {
    type: SEEKING_TYPES.MEMBERS,
    open: true,
    members: normalizeBandSeekingMembers(draft.members),
    bandStyles: [],
    notes: toCleanString(draft.notes),
    openToJam: false,
  };
};
